import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Identity } from 'models';

import {
    Card,
    Dropdown,
    DropdownItem,
    DropdownSort,
    FilterSetController,
    Table,
    TableCell,
    TableBody,
    TableRow,
    TableCellDropdown,
    ToastContext,
    LoaderCard
} from '@weezevent/nacre';

import { InfoTextTooltip, SauronEmptyCard } from 'components';
import { AddOrganizationModal } from './AddOrganizationModal';

import { matchSearch } from 'utils/utils';

import css from '../../emails.module.css';

const sortBaseKey = 'sauron.emails.domains.sort';

export const ActiveDomains = ({ domain, openModal, setOpenModal, syncDomain }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [ordering, setOrdering] = React.useState('id');
    const [search, setSearch] = React.useState('');

    const sortedOrganizations = React.useMemo(() => {
        let organizations = [...(domain.organizations ?? [])];

        if (search) {
            organizations = organizations.filter(orga => matchSearch([orga.id, orga.name], search));
        }

        switch (ordering) {
            case 'name':
                organizations.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case '-name':
                organizations.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case 'id':
                organizations.sort((a, b) => a.id - b.id);
                break;
            case '-id':
                organizations.sort((a, b) => b.id - a.id);
                break;
            default:
                return organizations;
        }

        return organizations;
    }, [domain.organizations, ordering, search]);

    const sortItems = [
        { key: 'id', label: t(`${sortBaseKey}.id`) },
        { key: '-id', label: t(`${sortBaseKey}.-id`) },
        { key: 'name', label: t(`${sortBaseKey}.a-z`) },
        { key: '-name', label: t(`${sortBaseKey}.z-a`) }
    ];

    return (
        <Card>
            <div className={css['card-header']}>
                <FilterSetController
                    className={css['filter-block']}
                    total={domain.organizations.length}
                    labelTotal={t('sauron.organizations.organization', { count: domain.organizations.length })}
                    onChange={() => {}}
                    search={{
                        onChange: (_, value) => setSearch(value),
                        handleReset: () => setSearch(''),
                        placeholder: t('sauron.emails.domains.search-organizations')
                    }}
                />
            </div>
            {sortedOrganizations.length > 0 ? (
                <Table>
                    <Table.THead>
                        <Table.Tr isHeader>
                            <TableCell className={css['active-domain-row']}>
                                <DropdownSort
                                    active={['id', '-id', 'name', '-name'].includes(ordering)}
                                    label={t(`common.organization`)}
                                    name="sort"
                                    ordering={ordering}
                                    items={sortItems.map(item => (
                                        <DropdownItem.Icons key={item.key} item={item.label} checked={ordering === item.key} onClick={() => setOrdering(item.key)} />
                                    ))}
                                />
                            </TableCell>
                            {domain?.is_success && <TableCell>{t(`sauron.emails.domains.actions.label`)}</TableCell>}
                        </Table.Tr>
                    </Table.THead>
                    {domain.is_success &&
                        sortedOrganizations.map((organization, index) => (
                            <TableBody key={index}>
                                <TableRow className={css['domains-table']}>
                                    <TableCell className={css['domain-row']}>
                                        <div className={css['organization-container']}>
                                            <span className={css['organization-name']}>{organization.name || '-'}</span>
                                            <span className={css['organization-id']}>O{organization.weez_orga_id}</span>
                                        </div>
                                    </TableCell>

                                    <TableCellDropdown>
                                        <Dropdown.Item
                                            key={0}
                                            item={t('sauron.emails.domains.actions.see-domains')}
                                            onClick={() => history.push(`/organizations/O${organization.id}/emails/domains`)}
                                        />
                                        <RemoveOrgaDropDown organizationId={organization.id} domain={domain} syncDomain={syncDomain} />
                                    </TableCellDropdown>
                                </TableRow>
                            </TableBody>
                        ))}
                    {openModal && <AddOrganizationModal open={openModal} onClose={() => setOpenModal(false)} domain={domain} syncDomain={syncDomain} />}
                </Table>
            ) : (
                <SauronEmptyCard subTitle={t('sauron.emails.domains.domain-info.no-organizations')} />
            )}
        </Card>
    );
};

const RemoveOrgaDropDown = ({ organizationId, domain, syncDomain }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [identities, loadingIdentities] = Identity.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [organizationId]
    );
    const handleDelete = React.useCallback(() => {
        domain.id = domain.name;
        domain.delete({ organizationId }).then(() => syncDomain());
    }, [domain, organizationId]);
    const isDomainUsed = domain?.isUsed(identities);
    return loadingIdentities ? (
        <Dropdown.Item key="loading">
            <div className={css['loader-wrapper']}>
                <LoaderCard active />
            </div>
        </Dropdown.Item>
    ) : (
        <InfoTextTooltip className={css['action-delete-tooltip']} title={t('sauron.emails.domains.actions.remove-domain.tool-tip')} tooltip={isDomainUsed}>
            <Dropdown.Item key={1} item={t('sauron.emails.domains.actions.remove-organization')} deleteItem onClick={handleDelete} disabled={isDomainUsed} />
        </InfoTextTooltip>
    );
};
